import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTranslation } from "react-i18next";

interface VehicleRecord {
  vehicle_id: string;
  plate?: string | null;
}

const TURKEY_CENTER: [number, number] = [39.5, 35.3];

// Marker positions until live GPS publisher feeds the map. Stable per-vehicle
// hash so a vehicle keeps roughly the same spot between renders.
function pseudoPosition(seed: string): [number, number] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const latOffset = ((h & 0xff) / 255) * 8 - 4; // ±4°
  const lngOffset = (((h >> 8) & 0xff) / 255) * 12 - 6; // ±6°
  return [TURKEY_CENTER[0] + latOffset, TURKEY_CENTER[1] + lngOffset];
}

export function OverviewMap({ vehicles }: { vehicles: VehicleRecord[] }) {
  const { t } = useTranslation("dashboard");
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, {
      attributionControl: false,
      zoomControl: false,
    }).setView(TURKEY_CENTER, 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
    }).addTo(map);
    L.control.zoom({ position: "bottomright" }).addTo(map);
    mapRef.current = map;
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const layer = L.layerGroup().addTo(map);
    const list = Array.isArray(vehicles) ? vehicles : [];
    list.slice(0, 50).forEach((v) => {
      if (!v || typeof v.vehicle_id !== "string") return;
      const pos = pseudoPosition(v.vehicle_id);
      const marker = L.circleMarker(pos, {
        radius: 7,
        color: "#2a5fff",
        fillColor: "#2a5fff",
        fillOpacity: 0.7,
        weight: 2,
      }).addTo(layer);
      marker.bindTooltip(v.plate || v.vehicle_id, {
        permanent: false,
        direction: "top",
      });
    });
    return () => {
      layer.remove();
    };
  }, [vehicles]);

  const hasVehicles = Array.isArray(vehicles) && vehicles.length > 0;
  return (
    <div className="overview-map-wrap">
      <div ref={ref} className="overview-map-canvas" />
      {!hasVehicles && (
        <div className="overview-map-empty">{t("panels.map.noVehicles")}</div>
      )}
      <div className="overview-map-note">{t("panels.map.note")}</div>
    </div>
  );
}
